<template>
  <div class="categories-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-title">Shop op Categorie</h1>
        <p class="hero-subtitle">
          Vind eenvoudig de producten die je zoekt door te browsen op categorie. 
          Van zonnepanelen tot laadpalen, wij hebben het allemaal.
        </p>
      </div>
    </section>
    
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Categorieën laden...</p>
    </div>
    
    <!-- Categories Grid -->
    <section v-else class="categories-section">
      <div class="container">
        
        <!-- Main Categories -->
        <div v-if="mainCategories.length > 0" class="categories-group">
          <h2 class="section-title">Hoofd Categorieën</h2>
          <div class="categories-grid">
            <router-link 
              v-for="category in mainCategories" 
              :key="category.id"
              :to="`/categorie/${category.slug}`"
              class="category-card main"
            >
              <div class="category-image">
                <img 
                  v-if="category.image"
                  :src="category.image.src" 
                  :alt="category.name"
                >
                <div v-else class="category-placeholder">
                  <i :class="getCategoryIcon(category.slug)"></i>
                </div>
              </div>
              
              <div class="category-content">
                <h3 class="category-name">{{ category.name }}</h3>
                <p v-if="category.description" class="category-description">
                  {{ stripHtml(category.description) }}
                </p>
                <div class="category-meta">
                  <span class="product-count">
                    <i class="bi bi-box-seam"></i>
                    {{ category.count }} {{ category.count === 1 ? 'product' : 'producten' }}
                  </span>
                  <span class="category-arrow">
                    <i class="bi bi-arrow-right"></i>
                  </span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
        
        <!-- All Categories (including subcategories) -->
        <div v-if="allCategories.length > 0" class="categories-group">
          <h2 class="section-title">Alle Categorieën</h2>
          <div class="categories-list">
            <router-link 
              v-for="category in allCategories" 
              :key="category.id"
              :to="`/categorie/${category.slug}`"
              class="category-list-item"
            >
              <div class="item-icon">
                <i :class="getCategoryIcon(category.slug)"></i>
              </div>
              <div class="item-content">
                <h4 class="item-name">{{ category.name }}</h4>
                <span class="item-count">{{ category.count }} producten</span>
              </div>
              <i class="bi bi-chevron-right item-arrow"></i>
            </router-link>
          </div>
        </div>
        
        <!-- Popular Categories -->
        <div v-if="popularCategories.length > 0" class="categories-group">
          <h2 class="section-title">
            <i class="bi bi-fire"></i>
            Populaire Categorieën
          </h2>
          <div class="popular-grid">
            <router-link 
              v-for="category in popularCategories" 
              :key="category.id"
              :to="`/categorie/${category.slug}`"
              class="popular-card"
            >
              <div class="popular-icon">
                <i :class="getCategoryIcon(category.slug)"></i>
              </div>
              <span class="popular-name">{{ category.name }}</span>
              <span class="popular-count">{{ category.count }}</span>
            </router-link>
          </div>
        </div>
        
      </div>
    </section>
    
    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <div class="cta-text">
            <h2>Hulp nodig bij je keuze?</h2>
            <p>Onze experts helpen je graag de perfecte producten te vinden voor jouw situatie.</p>
          </div>
          <div class="cta-actions">
            <a href="mailto:info@plug2charge.nl" class="cta-button primary">
              <i class="bi bi-envelope"></i>
              Stuur een mail
            </a>
            <a href="/klantenservice#livechat" class="cta-button secondary">
              <i class="bi bi-chat-dots"></i>
              Live chat
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useProductStore } from '@stores/products'

const productStore = useProductStore()

// Categorieën komen uit de gecachede productStore (30 min TTL)
const loading    = computed(() => productStore.loading)
const categories = computed(() => productStore.categories || [])

// Computed
const mainCategories = computed(() =>
  [...categories.value]
    .filter(cat => cat.parent === 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)
)

const allCategories = computed(() =>
  [...categories.value].sort((a, b) => a.name.localeCompare(b.name))
)

const popularCategories = computed(() =>
  [...categories.value]
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)
)

// Methods
const getCategoryIcon = (slug) => {
  const icons = {
    'zonnepanelen':       'bi bi-sun',
    'complete-sets':      'bi bi-box-seam',
    'balkonzonnepanelen': 'bi bi-sun',
    'laadpalen':          'bi bi-lightning-charge',
    'laadkabels':         'bi bi-plug',
    'wallboxen':          'bi bi-lightning-charge',
    'micro-omvormers':    'bi bi-gear',
    'omvormers':          'bi bi-gear',
    'thuisbatterijen':    'bi bi-battery',
    'accu':               'bi bi-battery',
    'montage':            'bi bi-hammer',
    'montage-materiaal':  'bi bi-hammer',
    'kabels':             'bi bi-plug',
    'kabels-connectoren': 'bi bi-plug',
    'monitoring':         'bi bi-bar-chart-line',
    'smart-home':         'bi bi-house',
    'accessoires':        'bi bi-gear',
    'gereedschap':        'bi bi-gear',
  }
  return icons[slug] || 'bi bi-folder'
}

const stripHtml = (html) => {
  if (!html) return ''
  const text = html.replace(/<[^>]*>/g, '')
  return text.length > 80 ? text.substring(0, 80) + '...' : text
}

// Lifecycle - gebruik gecachede fetch (doet niets als data < 30 min oud is)
onMounted(() => {
  productStore.fetchCategories()
  document.title = 'Categorieën | Plug2charge'
})
</script>

<style scoped>
/* ============================================
   CATEGORIES PAGE - SIMPLE & CLEAN
   ============================================ */

.categories-page {
  background: #f5f5f5;
  min-height: 100vh;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: #fff;
  padding: 4rem 0;
  text-align: center;
}

.hero-title {
  font-size: 3rem;
  font-weight: 900;
  margin-bottom: 1rem;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #ccc;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 3rem;
  height: 3rem;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #FFD500;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Categories Section */
.categories-section {
  padding: 3rem 0;
}

.categories-group {
  margin-bottom: 4rem;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-title i {
  color: #FFD500;
}

/* Main Categories Grid */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
}

.category-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  border: 2px solid transparent;
}

.category-card:hover {
  border-color: #FFD500;
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

.category-image {
  width: 100%;
  height: 200px;
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
}

.category-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.category-card:hover .category-image img {
  transform: scale(1.05);
}

.category-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: #ddd;
}

.category-content {
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.category-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.75rem;
}

.category-description {
  color: #666;
  font-size: 0.9375rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  flex: 1;
}

.category-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.product-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  font-size: 0.875rem;
}

.product-count i {
  color: #FFD500;
}

.category-arrow {
  color: #FFD500;
  font-size: 1.125rem;
  transition: transform 0.2s ease;
}

.category-card:hover .category-arrow {
  transform: translateX(5px);
}

/* All Categories List */
.categories-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.category-list-item {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.category-list-item:hover {
  border-color: #FFD500;
  background: #fff3cd;
  transform: translateX(8px);
}

.item-icon {
  width: 50px;
  height: 50px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #FFD500;
  flex-shrink: 0;
}

.item-content {
  flex: 1;
}

.item-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.25rem;
}

.item-count {
  font-size: 0.875rem;
  color: #666;
}

.item-arrow {
  color: #999;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.category-list-item:hover .item-arrow {
  color: #FFD500;
  transform: translateX(5px);
}

/* Popular Categories Grid */
.popular-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
}

.popular-card {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  transition: all 0.3s ease;
}

.popular-card:hover {
  border-color: #FFD500;
  transform: translateY(-8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.popular-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FFD500 0%, #FFC107 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: #000;
  transition: transform 0.3s ease;
}

.popular-card:hover .popular-icon {
  transform: scale(1.1);
}

.popular-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: #000;
}

.popular-count {
  font-size: 0.875rem;
  color: #666;
}

/* CTA Section */
.cta-section {
  background: #fff;
  padding: 4rem 0;
  margin-top: 2rem;
}

.cta-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3rem;
  padding: 3rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  border-radius: 16px;
  border: 2px solid #e9ecef;
}

.cta-text h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.75rem;
}

.cta-text p {
  font-size: 1.125rem;
  color: #666;
  margin: 0;
}

.cta-actions {
  display: flex;
  gap: 1rem;
  flex-shrink: 0;
}

.cta-button {
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.cta-button.primary {
  background: linear-gradient(135deg, #FFD500 0%, #FFC107 100%);
  color: #000;
  box-shadow: 0 4px 12px rgba(255, 213, 0, 0.3);
}

.cta-button.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 213, 0, 0.5);
}

.cta-button.secondary {
  background: #fff;
  color: #333;
  border: 2px solid #e9ecef;
}

.cta-button.secondary:hover {
  border-color: #FFD500;
  background: #fff3cd;
}

/* Responsive */
@media (max-width: 1200px) {
  .categories-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 991px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .cta-content {
    flex-direction: column;
    text-align: center;
  }
  
  .cta-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .cta-button {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 767px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .categories-grid {
    grid-template-columns: 1fr;
  }
  
  .categories-list {
    grid-template-columns: 1fr;
  }
  
  .popular-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .section-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .popular-grid {
    grid-template-columns: 1fr;
  }
}
</style>